// src/pages/GeofencingPage.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MapPin, ArrowLeft, Plus, Pencil, Trash2, Users, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const RADIUS_OPTIONS = [50, 100, 150, 200, 300, 500];

export const GeofencingPage = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { t, isRTL } = useLanguage();

  const [user, setUser] = useState(routerLocation.state?.user || null);

  const [company, setCompany] = useState(null);
  const [locations, setLocations] = useState([]);
  const autocompleteInputRef = useRef(null);
  const mapRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [unassignedEmployees, setUnassignedEmployees] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSavingLocation, setIsSavingLocation] = useState(false);
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [isDeletingLocation, setIsDeletingLocation] = useState(false);

  const [geofencingEnabled, setGeofencingEnabled] = useState(false);

  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);

  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationLatitude, setLocationLatitude] = useState("");
  const [locationLongitude, setLocationLongitude] = useState("");
  const [googleReady, setGoogleReady] = useState(false);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 24.7136, lng: 46.6753 });
  const [locationRadius, setLocationRadius] = useState("100");

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);

  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState([]);

  const [bulkEmployeeIds, setBulkEmployeeIds] = useState([]);
  const [bulkLocationIds, setBulkLocationIds] = useState([]);

  const fetchUser = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAll = async () => {
    setIsLoading(true);
    try {
      const [companyRes, locationsRes, employeesRes, unassignedRes] = await Promise.all([
        fetch(`${API_URL}/api/company`, { credentials: "include" }),
        fetch(`${API_URL}/api/company/locations`, { credentials: "include" }),
        fetch(`${API_URL}/api/employees/status`, { credentials: "include" }),
        fetch(`${API_URL}/api/company/unassigned-employees`, { credentials: "include" }),
      ]);

      if (companyRes.ok) {
        const companyData = await companyRes.json();
        setCompany(companyData);
        setGeofencingEnabled(!!companyData.geofencing_enabled);
      }

      if (locationsRes.ok) {
        const locationData = await locationsRes.json();
        setLocations(Array.isArray(locationData) ? locationData : []);
      }

      if (employeesRes.ok) {
        const employeeData = await employeesRes.json();
        setEmployees(Array.isArray(employeeData) ? employeeData : []);
      }

      if (unassignedRes.ok) {
        const unassignedData = await unassignedRes.json();
        setUnassignedEmployees(unassignedData.employees || []);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load geofencing data");
    } finally {
      setIsLoading(false);
    }
  };

  const geocodeAddress = (addressText) => {
    if (!window.google?.maps || !addressText?.trim()) return;
  
    const geocoder = new window.google.maps.Geocoder();
  
    geocoder.geocode({ address: addressText }, (results, status) => {
      if (
        status === "OK" &&
        results &&
        results.length > 0
      ) {
        const result = results[0];
        const lat = result.geometry.location.lat();
        const lng = result.geometry.location.lng();
  
        setLocationAddress(result.formatted_address || addressText);
        setLocationLatitude(String(lat));
        setLocationLongitude(String(lng));
        setMapCenter({ lat, lng });
  
        if (autocompleteInputRef.current) {
          autocompleteInputRef.current.value =
            result.formatted_address || addressText;
        }
  
        if (map) {
          map.setCenter({ lat, lng });
          map.setZoom(17);
        }
  
        if (marker) {
          marker.setPosition({ lat, lng });
        }
      }
    });
  };
  
  const reverseGeocode = (lat, lng) => {
    if (!window.google?.maps) return;
  
    const geocoder = new window.google.maps.Geocoder();
  
    geocoder.geocode(
      { location: { lat, lng } },
      (results, status) => {
        if (
          status === "OK" &&
          results &&
          results.length > 0
        ) {
          const formatted = results[0].formatted_address || "";
          setLocationAddress(formatted);
  
          if (autocompleteInputRef.current) {
            autocompleteInputRef.current.value = formatted;
          }
        }
      }
    );
  };
  useEffect(() => {
    if (!user) {
      fetchUser();
    }
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    if (window.google?.maps?.places) {
      setGoogleReady(true);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      console.error("Missing Google Maps API key");
      return;
    }

    const existing = document.getElementById("google-maps-script");
    if (existing) {
      existing.addEventListener("load", () => setGoogleReady(true));
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setGoogleReady(true);
    document.body.appendChild(script);
  }, [GOOGLE_MAPS_API_KEY]);

  useEffect(() => {
    const input = autocompleteInputRef.current;
    if (!input) return;
  
    const handleBlur = () => {
      if (input.value) {
        setLocationAddress(input.value);
      }
    };
  
    input.addEventListener("blur", handleBlur);
    return () => input.removeEventListener("blur", handleBlur);
  }, []);

  useEffect(() => {
    const lat = Number(locationLatitude);
    const lng = Number(locationLongitude);
  
    if (
      !window.google?.maps ||
      Number.isNaN(lat) ||
      Number.isNaN(lng)
    ) return;
  
    const timeout = setTimeout(() => {
      reverseGeocode(lat, lng);
    }, 500); // debounce
  
    return () => clearTimeout(timeout);
  }, [locationLatitude, locationLongitude]);
  
  useEffect(() => {
    if (!showLocationDialog || !googleReady || !window.google) return;
  
    const timer = setTimeout(() => {
      if (!mapRef.current) return;
  
      const center = {
        lat: Number(locationLatitude) || mapCenter.lat,
        lng: Number(locationLongitude) || mapCenter.lng,
      };
  
      const gMap = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });
  
      const gMarker = new window.google.maps.Marker({
        position: center,
        map: gMap,
        draggable: true,
      });
  
      gMarker.addListener("dragend", (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        setLocationLatitude(String(lat));
        setLocationLongitude(String(lng));
        setMapCenter({ lat, lng });
        reverseGeocode(lat, lng);
      });
  
      gMap.addListener("click", (event) => {
        const lat = event.latLng.lat();
        const lng = event.latLng.lng();
        gMarker.setPosition({ lat, lng });
        setLocationLatitude(String(lat));
        setLocationLongitude(String(lng));
        setMapCenter({ lat, lng });
        reverseGeocode(lat, lng);
      });
  
      if (autocompleteInputRef.current) {
        const autocomplete = new window.google.maps.places.Autocomplete(
          autocompleteInputRef.current,
          {
            fields: ["formatted_address", "geometry", "name"],
          }
        );
  
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place.geometry?.location) return;
  
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
  
          setLocationAddress(place.formatted_address || "");
          if (autocompleteInputRef.current && place.formatted_address) {
            autocompleteInputRef.current.value = place.formatted_address;
          }  
          
          if (!locationName.trim() && place.name) {
            setLocationName(place.name);
          }
  
          setLocationLatitude(String(lat));
          setLocationLongitude(String(lng));
          setMapCenter({ lat, lng });
  
          gMap.setCenter({ lat, lng });
          gMap.setZoom(17);
          gMarker.setPosition({ lat, lng });
        });
      }
  
      setMap(gMap);
      setMarker(gMarker);
  
      window.google.maps.event.trigger(gMap, "resize");
      gMap.setCenter(center);
    }, 300);
  
    return () => clearTimeout(timer);
  }, [showLocationDialog, googleReady, locationLatitude, locationLongitude, mapCenter, locationName]);

  const employeeMap = useMemo(() => {
    const map = {};
    employees.forEach((emp) => {
      map[emp.user_id] = emp;
    });
    return map;
  }, [employees]);

  const currentSelectedEmployee = useMemo(() => {
    return employees.find((emp) => emp.user_id === selectedEmployeeId) || null;
  }, [employees, selectedEmployeeId]);

  useEffect(() => {
    if (currentSelectedEmployee) {
      setSelectedAssignmentIds(currentSelectedEmployee.assigned_location_ids || []);
    } else {
      setSelectedAssignmentIds([]);
    }
  }, [currentSelectedEmployee]);

  const resetLocationForm = () => {
    setEditingLocation(null);
    setLocationName("");
    setLocationAddress("");
    setLocationLatitude("");
    setLocationLongitude("");
    setLocationRadius("100");
    setMapCenter({ lat: 24.7136, lng: 46.6753 });
  };

  const openCreateLocation = () => {
    resetLocationForm();
    setShowLocationDialog(true);
  };

  const openEditLocation = (loc) => {
    setEditingLocation(loc);
    setLocationName(loc.name || "");
    setLocationAddress(loc.address || "");
    setLocationLatitude(String(loc.latitude ?? ""));
    setLocationLongitude(String(loc.longitude ?? ""));
    setLocationRadius(String(loc.radius_meters ?? 100));
    setShowLocationDialog(true);
    setMapCenter({
      lat: Number(loc.latitude),
      lng: Number(loc.longitude),
    });
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/company`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          geofencing_enabled: geofencingEnabled,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save settings");
      }

      const updated = await res.json();
      setCompany(updated);
      toast.success("Geofencing settings saved");
    } catch (e) {
      toast.error(e.message || "Failed to save settings");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveLocation = async () => {
    if (!locationName.trim()) {
      toast.error("Location name is required");
      return;
    }
    if (!locationAddress.trim()) {
      toast.error("Address is required");
      return;
    }
    if (locationLatitude === "" || locationLongitude === "") {
      toast.error("Latitude and longitude are required");
      return;
    }

    const lat = Number(locationLatitude);
    const lng = Number(locationLongitude);

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast.error("Latitude and longitude must be valid numbers");
      return;
    }

    setIsSavingLocation(true);

    try {
      const payload = {
        name: locationName.trim(),
        address: locationAddress.trim(),
        latitude: lat,
        longitude: lng,
        radius_meters: Number(locationRadius),
      };

      const url = editingLocation
        ? `${API_URL}/api/company/locations/${editingLocation.location_id}`
        : `${API_URL}/api/company/locations`;

      const method = editingLocation ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save location");
      }

      toast.success(editingLocation ? "Location updated" : "Location created");
      setShowLocationDialog(false);
      resetLocationForm();
      await fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to save location");
    } finally {
      setIsSavingLocation(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!locationToDelete) return;

    setIsDeletingLocation(true);
    try {
      const res = await fetch(
        `${API_URL}/api/company/locations/${locationToDelete.location_id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(
          err.detail || "This location may still be assigned to employees"
        );
      }

      toast.success("Location deleted");
      setShowDeleteDialog(false);
      setLocationToDelete(null);
      await fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to delete location");
    } finally {
      setIsDeletingLocation(false);
    }
  };

  const handleSaveEmployeeAssignments = async () => {
    if (!selectedEmployeeId) {
      toast.error("Select an employee first");
      return;
    }

    setIsSavingAssignments(true);
    try {
      const res = await fetch(`${API_URL}/api/company/assignments`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_id: selectedEmployeeId,
          location_ids: selectedAssignmentIds,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to save assignments");
      }

      toast.success("Employee locations updated");
      await fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to save assignments");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  const toggleSelectedAssignment = (locationId) => {
    setSelectedAssignmentIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const toggleBulkEmployee = (userId) => {
    setBulkEmployeeIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleBulkLocation = (locationId) => {
    setBulkLocationIds((prev) =>
      prev.includes(locationId)
        ? prev.filter((id) => id !== locationId)
        : [...prev, locationId]
    );
  };

  const handleBulkAssign = async () => {
    if (bulkEmployeeIds.length === 0) {
      toast.error("Select at least one employee");
      return;
    }

    setIsSavingAssignments(true);
    try {
      const res = await fetch(`${API_URL}/api/company/assignments/bulk`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_ids: bulkEmployeeIds,
          location_ids: bulkLocationIds,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to bulk assign locations");
      }

      toast.success("Bulk assignment saved");
      setBulkEmployeeIds([]);
      setBulkLocationIds([]);
      await fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to bulk assign locations");
    } finally {
      setIsSavingAssignments(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background ${isRTL ? "rtl" : "ltr"}`}>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/employer")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <MapPin className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg md:text-xl">
                Geofencing
              </h1>
              <p className="text-xs text-muted-foreground">
                Manage company locations and employee assignments
              </p>
            </div>
          </div>
          <LanguageSelector />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Geofencing Settings</CardTitle>
            <CardDescription>
              Turn geofencing on or off for this company.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="font-medium">
                {geofencingEnabled ? "Geofencing is enabled" : "Geofencing is disabled"}
              </p>
              <p className="text-sm text-muted-foreground">
                Employees must be inside their assigned location radius to check in.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={geofencingEnabled}
                onCheckedChange={setGeofencingEnabled}
              />
              <Button onClick={handleSaveSettings} disabled={isSavingSettings}>
                {isSavingSettings ? "Saving..." : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {unassignedEmployees.length > 0 && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900">
                  Employees need location assignment
                </p>
                <p className="text-sm text-amber-800">
                  {unassignedEmployees.length} employee(s) cannot check in until assigned a
                  work location.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Company Locations</CardTitle>
                <CardDescription>
                  Create and manage allowed check-in locations.
                </CardDescription>
              </div>
              <Button onClick={openCreateLocation}>
                <Plus className="w-4 h-4 mr-2" />
                Add Location
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : locations.length === 0 ? (
                <div className="text-center py-10">
                  <MapPin className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="font-medium">No locations yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Add your first company location to start using geofencing.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {locations.map((loc) => (
                    <div
                      key={loc.location_id}
                      className="p-4 rounded-xl border bg-muted/30 flex items-start justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{loc.name}</p>
                          <Badge variant="outline">{loc.radius_meters}m</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {loc.address || "No address"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1 font-mono">
                          {loc.latitude}, {loc.longitude}
                        </p>
                        {!!loc.assigned_employee_count && (
                          <p className="text-xs text-primary mt-2">
                            Assigned employees: {loc.assigned_employee_count}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => openEditLocation(loc)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive"
                          onClick={() => {
                            setLocationToDelete(loc);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Assign Locations</CardTitle>
              <CardDescription>
                Choose one employee and assign one or more allowed locations.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Employee</Label>
                <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.user_id} value={emp.user_id}>
                        {emp.display_name || emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedEmployeeId && (
                <div className="space-y-3">
                  <Label>Allowed Locations</Label>
                  {locations.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Create locations first.
                    </p>
                  ) : (
                    locations.map((loc) => (
                      <label
                        key={loc.location_id}
                        className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedAssignmentIds.includes(loc.location_id)}
                          onCheckedChange={() => toggleSelectedAssignment(loc.location_id)}
                        />
                        <div>
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {loc.address} • {loc.radius_meters}m
                          </p>
                        </div>
                      </label>
                    ))
                  )}

                  <Button
                    onClick={handleSaveEmployeeAssignments}
                    disabled={isSavingAssignments}
                    className="w-full"
                  >
                    {isSavingAssignments ? "Saving..." : "Save Employee Assignments"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Bulk Assign / Reassign</CardTitle>
            <CardDescription>
              Select multiple employees and apply the same set of allowed locations.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4" />
                <p className="font-medium">Employees</p>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {employees.map((emp) => (
                  <label
                    key={emp.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
                  >
                    <Checkbox
                      checked={bulkEmployeeIds.includes(emp.user_id)}
                      onCheckedChange={() => toggleBulkEmployee(emp.user_id)}
                    />
                    <div>
                      <p className="font-medium">{emp.display_name || emp.name}</p>
                      <p className="text-xs text-muted-foreground">{emp.email}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4" />
                <p className="font-medium">Locations</p>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {locations.map((loc) => (
                  <label
                    key={loc.location_id}
                    className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer"
                  >
                    <Checkbox
                      checked={bulkLocationIds.includes(loc.location_id)}
                      onCheckedChange={() => toggleBulkLocation(loc.location_id)}
                    />
                    <div>
                      <p className="font-medium">{loc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {loc.address} • {loc.radius_meters}m
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="lg:col-span-2">
              <Button onClick={handleBulkAssign} disabled={isSavingAssignments}>
                {isSavingAssignments ? "Saving..." : "Apply Bulk Assignment"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
        <DialogContent>
          onInteractOutside={(e) => {
            const target = e.target;
            if (target.closest(".pac-container")) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? "Edit Location" : "Create Location"}
            </DialogTitle>
            <DialogDescription>
              Add a company location with address, coordinates, and radius.
            </DialogDescription>
          </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Location Name</Label>
                <Input
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="Main Office"
                />
              </div>
            
              <div className="space-y-2">
                <Label>Search Address</Label>
                <Input
                  ref={autocompleteInputRef}
                  placeholder="Search for an address"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      geocodeAddress(e.currentTarget.value);
                    }
                  }}
                  onBlur={(e) => {
                    const typed = e.currentTarget.value?.trim();
                    if (typed) {
                      geocodeAddress(typed);
                    }
                  }}
                />
                  
              </div>
            
              <div className="space-y-2">
                <Label>Pin Location</Label>
                <div
                  ref={mapRef}
                  className="w-full h-72 rounded-xl border overflow-hidden bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Search for an address, then drag the pin or click on the map.
                </p>
              </div>
            
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Latitude</Label>
                  <Input
                    value={locationLatitude}
                    onChange={(e) => setLocationLatitude(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Longitude</Label>
                  <Input value={locationLongitude} readOnly />
                </div>
              </div>
            
              <div className="space-y-2">
                <Label>Radius</Label>
                <Select value={locationRadius} onValueChange={setLocationRadius}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RADIUS_OPTIONS.map((meters) => (
                      <SelectItem key={meters} value={String(meters)}>
                        {meters} meters
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLocation} disabled={isSavingLocation}>
              {isSavingLocation ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
            <DialogDescription>
              Delete this location only if no employees are still assigned to it.
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            <p className="font-medium">{locationToDelete?.name}</p>
            <p className="text-sm text-muted-foreground mt-1">
              {locationToDelete?.address}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteLocation}
              disabled={isDeletingLocation}
            >
              {isDeletingLocation ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeofencingPage;
